import React from 'react';
import { isChunkLoadingError } from '@/utils/chunkLoadingErrorHandler';
import { getRecordingSnapshot } from '@/hooks/useSessionRecording';
import { submitAutoFeedbackReport } from '@/services/feedbackRecording.service';
import useFeedbackRecordingStore from '@/stores/feedbackRecording/feedbackRecordingStore';
import { isAutoReportingEnabled } from '@/utils/globalErrorHandler';
import DButton from '@/components/Global/DButton';

/**
 * Error Boundary that catches render crashes and submits automatic feedback reports.
 *
 * Chunk loading errors are re-thrown so ChunkLoadErrorBoundary (outer) handles them.
 * All other render errors are caught, reported, and a fallback UI is shown.
 */
class ReportingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    if (isChunkLoadingError(error)) {
      throw error;
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadingError(error)) {
      throw error;
    }

    const componentStack = (errorInfo.componentStack || '')
      .split('\n')
      .slice(0, 5)
      .join('\n');

    const errorContext = {
      message: error.message,
      endpoint: componentStack,
      method: 'RENDER_CRASH',
      statusCode: 0,
      timestamp: new Date().toISOString(),
    };

    if (!isAutoReportingEnabled()) return;

    useFeedbackRecordingStore.getState().setErrorContext(errorContext);

    setTimeout(() => {
      try {
        const recording = getRecordingSnapshot();
        const pathname = window.location.pathname;

        submitAutoFeedbackReport({
          recording,
          errorContext: {
            ...errorContext,
            endpoint: `${pathname} - ${componentStack}`,
          },
        });
      } catch (e) {
        console.debug('[ReportingErrorBoundary] Failed to submit auto-report:', e?.message);
      }
    }, 3000);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen w-screen flex justify-center items-center overflow-hidden" style={{ backgroundColor: '#0f172a', color: '#f8fafc' }}>
          <div className="max-w-3xl px-6 md:px-8 py-12 bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg" style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="flex flex-col gap-4 items-center text-center">
              <h1 className="text-3xl md:text-4xl font-semibold mb-2">
                Something went wrong
              </h1>
              <p className="text-base md:text-lg mb-4 text-gray-200/90 max-w-lg">
                An unexpected error occurred. Our team has been automatically notified and is looking into it. You can try reloading the page or going back to the home page.
              </p>
              <div className="flex gap-3">
                <DButton
                  onClick={() => window.location.reload()}
                  variant="contained"
                  color="primary"
                  size="sm"
                  style={{
                    fontWeight: 600,
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
                    padding: '12px 24px',
                  }}
                >
                  Reload page
                </DButton>
                <DButton
                  onClick={() => { window.location.href = '/'; }}
                  variant="outlined"
                  color="primary"
                  size="sm"
                  style={{
                    fontWeight: 600,
                    padding: '12px 24px',
                  }}
                >
                  Go to home
                </DButton>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ReportingErrorBoundary;
